import React from 'react'
import html2canvas from 'html2canvas'
import * as Yup from 'yup'

import { format } from 'date-fns'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
} from '@mui/material'
import { CloudUpload as CloudUploadIcon, Save as SaveIcon } from '@mui/icons-material'
import { Form } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { makeValidate, Radios, TextField } from 'mui-rff'

import { isVehicleSearchCard } from '../utils/isVehicleSearchCard'
import { useActiveOrganization, useCurrentUserAttributes } from '@edge/spark/io-roles'
import { useSearchParams } from '@edge/spark/utils'

import { useSharePdfCsvMutation, useCsvLinkQuery } from '../generated/graphql'
import { s3Upload } from '@edge/spark/io-aws'
import { shorten } from './bitlyLib'
import { CustomPhoneInput, ShareModalButton } from '@edge/spark/ui'
import { ShareModalFormData } from './ShareModalFormData'

const PDFBlob = React.lazy(() => import('./PDFRender'))

interface PDFBlobProviderParams {
    url?: string | null
    blob?: Blob | null
}

export interface ShareMetaData {
    shareSubject?: string
}

const useChartImageFromRef = (ref?: React.RefObject<any>): string => {
    // @ts-expect-error html2canvas needs to be globally available
    if (!window.html2canvas) {
        // @ts-expect-error html2canvas needs to be globally available
        window.html2canvas = html2canvas
    }

    const [chartImgSrc, setChartImgSrc] = React.useState('')

    React.useEffect(() => {
        ;(async () => {
            if (ref?.current) {
                const canvas = await html2canvas(ref?.current, {
                    scale: 3,
                })
                setChartImgSrc(canvas.toDataURL('image/png'))
            }
        })()
    }, [ref])

    return chartImgSrc
}

interface PDFRenderProps {
    chartOnly?: boolean
    columns: any
    data: any[]
    subtitle?: string
    title: string
    chartRef?: React.RefObject<any>
    secondaryFilter?: string
    shareMetaData?: ShareMetaData
}

interface ShareModalProps extends PDFRenderProps {
    onClose: () => void
}

export const ShareModal: React.FC<ShareModalProps> = ({
    chartOnly, // TODO: move to shareMetaData
    chartRef, // TODO: move to shareMetaData
    secondaryFilter,
    columns,
    data: tableData,
    onClose,
    subtitle,
    title,
    shareMetaData,
}: ShareModalProps) => {
    const { search } = useSearchParams()
    const searchString = `${search}${secondaryFilter ? ` ${secondaryFilter}` : ''}`
    const isSearchCard = isVehicleSearchCard()

    const [filetype, setFiletype] = React.useState('pdf')
    const [pdf, setPDF] = React.useState<Partial<Pick<PDFBlobProviderParams, 'url' | 'blob'>>>({})

    const chartImgSrc = useChartImageFromRef(chartRef)

    const [sharePdf, { loading: mutationLoading }] = useSharePdfCsvMutation()
    const { name: userFirstName, familyName: userLastName, email: userEmail } = useCurrentUserAttributes()

    const { data: csvLinkData, loading: csvLoading } = useCsvLinkQuery({
        variables: { searchString },
        skip: filetype !== 'csv',
    })

    const activeOrg = useActiveOrganization()
    const auctionId = activeOrg?.orgId || ''
    const auctionName = activeOrg?.displayName || ''
    const vehicleResultCsvUrl = csvLinkData?.auction?.search?.vehicleResultCsvUrl || ''

    const disableDownload = (filetype === 'pdf' && !pdf?.url) || (filetype === 'csv' && !vehicleResultCsvUrl)
    const loading = mutationLoading || csvLoading

    const date = format(new Date(), 'yyyy-MM-dd_HH-mm-ss')
    const downloadFilename = `${auctionId.toUpperCase()}-${date}.${filetype}`

    const handleClickDownload = (evt: React.MouseEvent): void => {
        evt.preventDefault()

        const a = document.createElement('a')
        a.download = downloadFilename
        a.href = (filetype === 'csv' ? vehicleResultCsvUrl : pdf?.url) || '#'
        a.click()
    }

    const handleClickSend = async (values: ShareModalFormData): Promise<void> => {
        const { email, phone, message, fileType } = values
        const pdfBlob = pdf?.blob

        if (!pdfBlob) {
            throw new Error('No PDF blob')
        }

        const messageName = shareMetaData?.shareSubject

        const largeUrl = fileType === 'csv' ? vehicleResultCsvUrl : await s3Upload(downloadFilename, pdfBlob)
        const shortUrl = await shorten(largeUrl)
        const displayUrl = `Download Link: ${shortUrl}`
        const messageBody = message || ''

        await sharePdf({
            variables: {
                displayUrl,
                emailAddress: email,
                smsNumber: phone,
                messageBody,
                subject: messageName ? `${messageName} from ${auctionName}` : `From ${auctionName}`,
                senderEmail: userEmail,
                senderName: `${userFirstName} ${userLastName}`,
            },
        })

        onClose()
    }
    const handlePDFRenderCompleted = React.useMemo(
        () =>
            ({ url, blob }: PDFBlobProviderParams): void => {
                if (url) {
                    setPDF({ url, blob })
                }
            },
        [setPDF],
    )
    const phoneRegex = /\d{10}/
    const schema = Yup.object({
        email: Yup.string().email('Must be valid email'),
        phone: Yup.string().matches(phoneRegex, 'Must be valid phone'),
    })
    const validate = makeValidate(schema)

    return (
        <>
            {auctionName && (
                <React.Suspense fallback={null}>
                    <PDFBlob
                        auctionName={auctionName}
                        chartImgSrc={chartImgSrc}
                        columns={columns}
                        data={tableData}
                        subtitle={subtitle}
                        title={title}
                        onCompleted={handlePDFRenderCompleted}
                        chartOnly={chartOnly}
                    />
                </React.Suspense>
            )}
            <Form<ShareModalFormData>
                onSubmit={handleClickSend}
                validate={validate}
                initialValues={{ fileType: 'pdf' }}
                render={({ handleSubmit, values, submitting, hasValidationErrors }) => (
                    <Dialog onClose={onClose} open>
                        <DialogTitle>{title}</DialogTitle>

                        <DialogContent>
                            {isSearchCard && (
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Filetype</FormLabel>
                                    <Radios
                                        name="fileType"
                                        data={[
                                            { label: 'PDF (100 Row Max)', value: 'pdf' },
                                            { label: 'CSV (50k Row Max)', value: 'csv' },
                                        ]}
                                    />
                                    <OnChange name="fileType">
                                        {(value) => {
                                            setFiletype(value)
                                        }}
                                    </OnChange>
                                </FormControl>
                            )}
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    disabled={loading}
                                    label="Email"
                                    name="email"
                                    type="email"
                                    variant="outlined"
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <TextField
                                    disabled={loading}
                                    fullWidth
                                    label="Mobile Number"
                                    name="phone"
                                    margin="normal"
                                    type="tel"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        inputComponent: CustomPhoneInput,
                                    }}
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <TextField
                                    multiline
                                    label="Message"
                                    name="message"
                                    placeholder="Message"
                                    margin="normal"
                                    rows="4"
                                    variant="outlined"
                                />
                            </FormControl>
                            <DialogActions>
                                <Button onClick={onClose}>Cancel</Button>
                                <ShareModalButton
                                    disabled={disableDownload}
                                    onClick={handleClickDownload}
                                    startIcon={csvLoading ? <CircularProgress size={24} /> : <SaveIcon />}
                                >
                                    Download
                                </ShareModalButton>
                                <ShareModalButton
                                    color="primary"
                                    disabled={
                                        (!values.email && !values.phone) || submitting || hasValidationErrors || loading
                                    }
                                    onClick={handleSubmit}
                                    startIcon={mutationLoading ? <CircularProgress size={24} /> : <CloudUploadIcon />}
                                    type="submit"
                                >
                                    Send
                                </ShareModalButton>
                            </DialogActions>
                        </DialogContent>
                    </Dialog>
                )}
            />
        </>
    )
}

interface ToggleShareModalProps extends ShareModalProps {
    open: boolean
}

export const ToggleShareModal: React.FC<ToggleShareModalProps> = ({ open, ...rest }: ToggleShareModalProps) =>
    open ? <ShareModal {...rest} /> : <div />
